import React, { useEffect, useState, useRef } from "react";
import { Box, Typography } from "@mui/material";
import { useWorkbookRestore } from "./ui/hooks/useWorkbookRestore";
import { LAST_PROJECT_LOAD_ERROR } from "./ui/constants";
import LeaderboardOutlinedIcon from "@mui/icons-material/LeaderboardOutlined";
import CloudDownloadOutlinedIcon from "@mui/icons-material/CloudDownloadOutlined";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import Header from "./ui/components/nav/Header";
import Subnav from "./ui/components/nav/Subnav";
import ProjectHeader from "./ui/components/project/ProjectHeader";
import DataPage from "./ui/pages/Data";
import InsightsPage from "./ui/pages/Insights";
import LoginPage from "./ui/pages/Login";
import ReportsPage from "./ui/pages/Reports";
import ImportPage from "./ui/pages/Import";
import UserSettingsPage from "./ui/pages/UserSettings";
import { useFeatureFlag } from "./ui/hooks/useFeatureFlag";
import { BannerProvider, useBanner } from "./ui/context/BannerContext";
import DialogHost from "./ui/shared/dialog/DialogHost";
import BannerHost from "./ui/shared/banner/BannerHost";
import { LoadingProvider } from "./ui/context/LoadingContext";
import { UserSettingsProvider } from "./ui/context/UserSettingsContext";
import { isUserAdmin, tokenIsValid } from "./clients/auth";
import { grey } from "@mui/material/colors";
import { ediphiPalette } from "./ui/styles/colors";
import { SCROLLBAR_WIDTH, thinScrollbarSx } from "./ui/styles/scrollbar";
import { ProjectProvider, useProject } from "./ui/context/ProjectContext";
import { DatasourceProvider } from "./ui/context/DatasourceContext";
import { WorksheetListenerProvider } from "./ui/context/WorksheetListenerContext";
import { track } from "./clients/mixpanel";
import { DialogProvider } from "./ui/context/DialogContext";
import { ReportDataProvider } from "./ui/context/ReportDataContext";
import { LaunchDarklyProvider, useLaunchDarklyIdentify } from "./ui/context/LaunchDarklyContext";
import { SESSION_EXPIRED_EVENT } from "./utils/session";
import { VERSION } from "./config/version";
import QtoIntegrationProviders from "./ui/components/takeoff/shared/QtoIntegrationProviders";
import { browserStore } from "./utils/storageKeys";

const AppContent: React.FC = () => {
  const [activePage, setActivePage] = useState<string>("Login");
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const { selectedProject } = useProject();
  const prevSelectedProjectRef = useRef<typeof selectedProject>(selectedProject);
  // Mirror isLoggedIn into a ref so the session-expired listener (registered once) can read the
  // current value without re-subscribing on every login state change.
  const isLoggedInRef = useRef<boolean>(isLoggedIn);
  isLoggedInRef.current = isLoggedIn;
  const arcolImportEnabled = useFeatureFlag("last-mile-enable-arcol-import");
  const identifyLaunchDarkly = useLaunchDarklyIdentify();
  const { showBanner } = useBanner();
  const { restoreFromWorkbook } = useWorkbookRestore();

  useEffect(() => {
    const bootLoader = document.getElementById("boot-loader");
    if (!bootLoader) return;
    bootLoader.classList.toggle("boot-loader--hidden", !loading);
    bootLoader.setAttribute("aria-hidden", String(!loading));
  }, [loading]);

  useEffect(() => {
    console.log("Checking if token is valid...");
    tokenIsValid().then(async (valid: boolean) => {
      console.log(valid ? "Token is valid:" : "Token is INVALID");
      const signedIn = valid && (await restoreSelection());
      setIsLoggedIn(signedIn);
      setActivePage(signedIn ? "Data" : "Login");
      setLoading(false);
    });
  }, []);

  // A previously valid session token was rejected by the API — it was revoked (a web-app logout
  // revokes all of a user's tokens, including the one the add-in scraped) or it expired. Drop the
  // dead credentials so the mount-time tokenIsValid check can't loop on them, then route back to a
  // clean login. The banner only shows if the user believed they were signed in; a 401 during the
  // initial token check just means the stored token was already stale.
  useEffect(() => {
    const handleSessionExpired = () => {
      const wasLoggedIn = isLoggedInRef.current;
      // Clear the ref synchronously (not just via setIsLoggedIn, which only updates it on the next
      // render). A revoked token usually fails several in-flight requests at once, each firing this
      // event in the same tick; without this, every one still reads wasLoggedIn === true and stacks
      // another banner.
      isLoggedInRef.current = false;
      browserStore.remove("xAuthToken");
      browserStore.remove("idToken");
      browserStore.remove("userProfile");
      browserStore.remove("userEmail");
      setIsLoggedIn(false);
      setActivePage("Login");
      browserStore.set("activePage", "Login");
      if (wasLoggedIn) {
        showBanner("Your session has expired. Please log in again.", "error");
      }
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
  }, [showBanner]);

  // Navigate to Data page when selectedProject is cleared
  useEffect(() => {
    // Only navigate if project was cleared (changed from a project to null)
    const wasProjectCleared = prevSelectedProjectRef.current !== null && selectedProject === null;

    if (wasProjectCleared && isLoggedIn && activePage !== "Data") {
      setActivePage("Data");
      browserStore.set("activePage", "Data");
    }

    // Update ref for next comparison
    prevSelectedProjectRef.current = selectedProject;
  }, [selectedProject, isLoggedIn, activePage]);

  /** Restores the workbook's selection. False when a request during it found the session expired. */
  const restoreSelection = async (): Promise<boolean> => {
    try {
      await restoreFromWorkbook();
    } catch {
      showBanner(LAST_PROJECT_LOAD_ERROR, "error");
    }
    // The session-expired handler drops the token, so a 401 during the restore leaves none.
    return Boolean(browserStore.get("xAuthToken"));
  };

  const handleLoginSuccess = async () => {
    // Re-identify so flags evaluate against the tenant/profile login just stored, instead of the
    // anonymous defaults the context mounted with. Fire-and-forget: the SDK emits change events
    // that useFeatureFlag listens for, so gated components appear once the new context resolves.
    void identifyLaunchDarkly();
    setLoading(true);
    const signedIn = await restoreSelection();
    setLoading(false);
    // Signed out again during the restore: the session-expired handler has already routed to login.
    if (!signedIn) return;
    setIsLoggedIn(true);
    setActivePage("Data");
    browserStore.set("activePage", "Data");
  };

  const handleButtonClick = (name: string) => {
    setActivePage(name);
    browserStore.set("activePage", name);
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setActivePage("");
    browserStore.remove("activePage");
  };

  const renderPage = () => {
    // Calculate app load time when the page is actually rendered
    const startTimeStr = sessionStorage.getItem("app_load_start");
    if (startTimeStr) {
      const startTime = parseFloat(startTimeStr);
      const endTime = performance.now();
      const totalDuration = (endTime - startTime) / 1000;
      track("excel.app.render", { loadTime: totalDuration });
      sessionStorage.removeItem("app_load_start");
    }

    if (!isLoggedIn) {
      return <LoginPage onLoginSuccess={handleLoginSuccess} />;
    }

    switch (activePage) {
      case "Data":
        return <DataPage />;
      case "Insights":
        return <InsightsPage />;
      case "Reports":
        return <ReportsPage />;
      case "Import":
        return arcolImportEnabled ? <ImportPage /> : <DataPage />;
      case "Profile":
      case "Admin":
        return <UserSettingsPage onLogOut={handleLogout} />;
      default:
        return <LoginPage onLoginSuccess={handleLoginSuccess} />;
    }
  };

  if (loading) {
    return <Box sx={{ backgroundColor: ediphiPalette.mainBackground, height: "100vh" }} />;
  }

  return (
    <Box
      sx={{
        backgroundColor: ediphiPalette.mainBackground,
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        width: "100%",
        maxWidth: "100vw",
      }}
    >
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 1000,
          backgroundColor: ediphiPalette.surface,
          width: "100%",
        }}
      >
        {(isLoggedIn && (
          <Box sx={{ width: "100%" }}>
            <Subnav
              buttons={[
                { name: "Data", icon: <CloudDownloadOutlinedIcon /> },
                { name: "Insights", icon: <LeaderboardOutlinedIcon /> },
                { name: "Reports", icon: <InsertDriveFileOutlinedIcon /> },
                ...(arcolImportEnabled ? [{ name: "Import", icon: <FileUploadOutlinedIcon /> }] : []),
                { name: isUserAdmin() ? "Admin" : "Profile", icon: <AccountCircleOutlinedIcon /> },
              ]}
              onButtonClick={handleButtonClick}
              activePage={activePage}
            />
            {(activePage === "Data" || activePage === "Reports" || activePage === "Insights") && <ProjectHeader />}
          </Box>
        )) || <Header />}
      </Box>
      <Box
        sx={{
          flex: 1,
          // `scroll`, not `auto`: a classic scrollbar takes layout width, so appearing on
          // overflow would narrow the content and shift centered layouts left. Reserving it
          // always costs the same space but never moves anything.
          overflowY: "scroll",
          padding: "16px",
          pt: 0,
          // The reserved scrollbar sits inside padding-right, so trim the padding by its width
          // to keep both gutters at 16px. Only valid while thinScrollbarSx pins that width.
          paddingRight: `${16 - SCROLLBAR_WIDTH}px`,
          width: "100%",
          ...thinScrollbarSx,
        }}
      >
        {renderPage()}
      </Box>
      <Box display="flex" justifyContent="center" alignItems="center">
        <Typography variant="caption" color={grey[500]} textAlign="center">
          {VERSION}
        </Typography>
      </Box>
    </Box>
  );
};

export const App: React.FC = () => {
  return (
    <LoadingProvider>
      <DialogProvider>
        <DialogHost />
        <BannerProvider>
          <BannerHost />
          <ProjectProvider>
            <QtoIntegrationProviders>
              <UserSettingsProvider>
                <LaunchDarklyProvider>
                  <DatasourceProvider>
                    <ReportDataProvider>
                      <WorksheetListenerProvider>
                        <AppContent />
                      </WorksheetListenerProvider>
                    </ReportDataProvider>
                  </DatasourceProvider>
                </LaunchDarklyProvider>
              </UserSettingsProvider>
            </QtoIntegrationProviders>
          </ProjectProvider>
        </BannerProvider>
      </DialogProvider>
    </LoadingProvider>
  );
};
